import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import WarningStep from "./WarningStep";
import SettingStep from "./SettingStep";
import ResultStep from "./ResultStep";
import styles from "./AiWrite.module.scss";

const STEP = {
    WARNING: "warning",
    SETTING: "setting",
    RESULT: "result",
};

const AiWritePage = ({
    propertyInfo = {},
    existingContent = "",
    estateCategory = "residential",
    eRandType,
    isMobile = false,
    onApply = () => {},
    onClose = () => {},
}) => {
    const [step, setStep] = useState(
        existingContent.trim() ? STEP.WARNING : STEP.SETTING
    );
    const [remainCount, setRemainCount] = useState(null);
    const [generatedText, setGeneratedText] = useState("");
    const [isAnimating, setIsAnimating] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setIsAnimating(true), 10);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const { body, documentElement: html } = document;
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;
        const prev = {
            bodyPosition: body.style.position,
            bodyTop: body.style.top,
            bodyLeft: body.style.left,
            bodyRight: body.style.right,
            bodyWidth: body.style.width,
            bodyOverflow: body.style.overflow,
            htmlOverflow: html.style.overflow,
            htmlOverscroll: html.style.overscrollBehavior,
        };
        body.style.position = "fixed";
        body.style.top = `-${scrollY}px`;
        body.style.left = `-${scrollX}px`;
        body.style.right = "0";
        body.style.width = "100%";
        body.style.overflow = "hidden";
        html.style.overflow = "hidden";
        html.style.overscrollBehavior = "none";
        return () => {
            body.style.position = prev.bodyPosition;
            body.style.top = prev.bodyTop;
            body.style.left = prev.bodyLeft;
            body.style.right = prev.bodyRight;
            body.style.width = prev.bodyWidth;
            body.style.overflow = prev.bodyOverflow;
            html.style.overflow = prev.htmlOverflow;
            html.style.overscrollBehavior = prev.htmlOverscroll;
            window.scrollTo(scrollX, scrollY);
        };
    }, []);

    useEffect(() => {
        if (!isClosing) return;
        setIsAnimating(false);
        const timer = setTimeout(onClose, 300);
        return () => clearTimeout(timer);
    }, [isClosing, onClose]);

    const handleClose = useCallback(() => {
        setIsClosing(true);
    }, []);

    const handleConfirmWarning = useCallback(() => {
        setStep(STEP.SETTING);
    }, []);

    const handleGenerated = useCallback((text, count) => {
        setGeneratedText(text);
        setRemainCount(count);
        setStep(STEP.RESULT);
    }, []);

    const handleRegenerate = useCallback(() => {
        setStep(STEP.SETTING);
    }, []);

    const handleApply = useCallback((text) => {
        onApply(text);
        handleClose();
    }, [onApply, handleClose]);

    const handleOverlayClick = useCallback(() => {
        handleClose();
    }, [handleClose]);

    const isMobileWarningPopup = isMobile && step === STEP.WARNING;
    const useFullscreen = isMobile && !isMobileWarningPopup;
    const showOverlay = !isMobile || isMobileWarningPopup;

    return createPortal(
        <>
            {showOverlay && (
                <div
                    className={`${styles.overlay} ${isAnimating ? styles.show : ""}`}
                    onClick={handleOverlayClick}
                />
            )}
            <div className={`${useFullscreen ? styles.fullscreen : styles.popup} ${isAnimating ? styles.show : ""}`}>
                <div className={`${styles.container} ${useFullscreen ? styles.mobile : ""} ${isMobileWarningPopup ? styles.mobilePopup : ""}`}>
                    {step === STEP.WARNING && (
                        <WarningStep
                            onConfirm={handleConfirmWarning}
                            onClose={handleClose}
                        />
                    )}
                    {step === STEP.SETTING && (
                        <SettingStep
                            propertyInfo={propertyInfo}
                            estateCategory={estateCategory}
                            eRandType={eRandType}
                            remainCount={remainCount}
                            onGenerated={handleGenerated}
                            onClose={handleClose}
                        />
                    )}
                    {step === STEP.RESULT && (
                        <ResultStep
                            text={generatedText}
                            remainCount={remainCount}
                            onRegenerate={handleRegenerate}
                            onApply={handleApply}
                            onClose={handleClose}
                        />
                    )}
                </div>
            </div>
        </>,
        document.body
    );
};

export default AiWritePage;
